import { Button, CircularProgress, Divider, Grid, Stack, Typography } from '@mui/material';
import React, { useCallback, useEffect } from 'react';
import { useFieldArray, useWatch } from 'react-hook-form';
import { FormComponentProps } from 'src/components/FormPart/types';
import { ContactDefaultCustomField } from 'src/constants/types';
import { Group, GroupFormData } from '../../types';
import { DynamicGroupRulePart } from './DynamicGroupRulePart';

export type DynamicGroupRulesFormProps = {
  customFields: ContactDefaultCustomField[];
  customGroups: Group[];
  isContactsCountInfoLoading: boolean;
  contactsCountInfo: {
    status: 'success' | 'error';
    validContactsCount?: number;
    invalidContactsCount?: number;
  } | null;
  closeForm: () => void;
  testRules: (rules: GroupFormData['rules'] | null) => void;
};

export const DynamicGroupRulesForm = (
  props: FormComponentProps<GroupFormData, DynamicGroupRulesFormProps>,
) => {
  const {
    form,
    customFields,
    customGroups,
    contactsCountInfo,
    closeForm,
    isContactsCountInfoLoading,
    testRules,
  } = props;

  const { fields, append, remove } = useFieldArray<GroupFormData, 'rules', 'fieldId'>({
    control: form.control,
    keyName: 'fieldId',
    name: 'rules',
  });

  const rules = useWatch({
    control: form.control,
    name: 'rules',
    defaultValue: fields,
  });

  const addRule = useCallback(async () => {
    const isValid = await form.trigger();

    if (isValid) {
      append({ fieldName: '', operator: '', values: [''] });
    }
  }, []);

  const checkRules = useCallback(async (rules: GroupFormData['rules'], showErrors: boolean = true) => {
    const isValid = await form.trigger();
    if (!showErrors) {
      form.clearErrors();
    }

    testRules(isValid ? rules : rules.length ? null : []);
  }, []);

  useEffect(() => {
    checkRules(rules, false);
  }, [rules]);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        {fields.map((f, i) => {
          return (
            <React.Fragment key={f.fieldId}>
              <DynamicGroupRulePart
                form={form}
                fieldIndex={i}
                customFields={customFields}
                customGroups={customGroups}
                remove={remove}
              />
              {i !== fields.length - 1 && (
                <Divider textAlign="center" sx={{ my: 1.5 }}>
                  And
                </Divider>
              )}
            </React.Fragment>
          );
        })}
      </Grid>

      {fields.length > 0 && (
        <Grid item xs={12}>
          <Stack direction="row" alignItems="center" justifyContent="flex-end" height={56} overflow="hidden">
            {isContactsCountInfoLoading ? (
              <CircularProgress size={12} />
            ) : contactsCountInfo?.status === 'success' ? (
              <Typography variant="body2" color="primary.main" textAlign="end">
                Valid contacts: {contactsCountInfo?.validContactsCount} <br />
                Invalid contacts: {contactsCountInfo?.invalidContactsCount}
              </Typography>
            ) : contactsCountInfo?.status === 'error' ? (
              <Typography variant="body2" color="error" textAlign="end">
                Unable to calculate contacts. Please check your rules.
              </Typography>
            ) : null}
          </Stack>
        </Grid>
      )}

      <Grid item xs={12}>
        <Button
          onClick={addRule}
          variant="contained"
          color="secondary"
          disabled={contactsCountInfo?.status === 'error'}
          fullWidth
        >
          Add New Rule
        </Button>
      </Grid>

      <Grid item xs={12}>
        <Grid container spacing={1} alignItems="center" justifyContent="flex-end">
          <Grid item>
            <Button onClick={closeForm} variant="contained" color="error" fullWidth>
              Cancel
            </Button>
          </Grid>

          <Grid item>
            <Button
              type="submit"
              disabled={
                !fields.length ||
                form.formState.isSubmitting ||
                contactsCountInfo?.status === 'error'
              }
              variant="contained"
              color="secondary"
              fullWidth
            >
              Save
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};
